import React from 'react';
import { useLlmConfig } from '../../../contexts/LlmConfigContext';

const TOGGLES = [
  { key: 'enabled', label: 'LLM Safety Enabled', description: 'Master switch for all LLM safety checks' },
  { key: 'enable_precheck', label: 'Pre-check', description: 'Run safety checks before sending to LLM' },
  { key: 'enable_postcheck', label: 'Post-check', description: 'Run safety checks on LLM output' },
  { key: 'enable_pii_check', label: 'PII Check', description: 'Detect and filter personally identifiable information' },
  { key: 'enable_prompt_injection_check', label: 'Prompt Injection Check', description: 'Detect prompt injection attacks' },
];

const THRESHOLDS = [
  { key: 'pii_score_threshold', label: 'PII Score Threshold', min: 0, max: 1, step: 0.01 },
  { key: 'injection_score_threshold', label: 'Injection Score Threshold', min: 0, max: 1, step: 0.01 },
];

const Toggle = ({ enabled, onChange }) => (
  <button
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
      enabled ? 'bg-primary' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        enabled ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const LlmConfig = () => {
  const { llmConfig, setLlmConfig } = useLlmConfig();

  const handleToggle = (key, value) => {
    setLlmConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleThreshold = (key, value) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      setLlmConfig((prev) => ({ ...prev, [key]: Math.min(1, Math.max(0, num)) }));
    }
  };

  return (
    <div className="mt-6 max-w-2xl">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">LLM Safety Configuration</h2>
        <p className="text-sm text-gray-500 mb-6">
          These settings apply to the Wealth Chatbot v2 and are saved for all users.
        </p>

        <div className="space-y-5">
          {TOGGLES.map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <p className="text-xs text-gray-500">{description}</p>
              </div>
              <Toggle
                enabled={llmConfig[key]}
                onChange={(val) => handleToggle(key, val)}
              />
            </div>
          ))}
        </div>

        <div className="mt-8 border-t border-gray-100 pt-6 space-y-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Score Thresholds</h3>
          {THRESHOLDS.map(({ key, label, min, max, step }) => (
            <div key={key} className="flex items-center justify-between gap-6">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800 mb-1">{label}</p>
                <input
                  type="range"
                  min={min}
                  max={max}
                  step={step}
                  value={llmConfig[key]}
                  onChange={(e) => handleThreshold(key, e.target.value)}
                  className="w-full accent-primary"
                />
              </div>
              <input
                type="number"
                min={min}
                max={max}
                step={step}
                value={llmConfig[key]}
                onChange={(e) => handleThreshold(key, e.target.value)}
                className="w-20 text-sm border border-gray-300 rounded-lg px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LlmConfig;
